import { z } from 'zod';
import { ChecklistCompleteRequest, ChecklistOwner } from './checklists';

// Fellow stipends (/api/stipends). Schemas check shape only: readiness, batch size, the note
// guard and lookups stay in stipendService. The lists mirror lib/stipendRules.ts, the stipend
// checklist templates and the service's message kinds (the tests compare them).

const RowId = z.string().regex(/^[0-9a-f-]{36}$/i, 'Not an id');
const Text = z.string().nullish();

export const StipendRoute = z.enum(['tof', 'york_student', 'york_ecr']);
export type StipendRoute = z.infer<typeof StipendRoute>;

/** Steps of the Transfer of Funds checklist done once per agreement for every Fellow on it. */
export const StipendSharedStepKey = z.enum([
  'tof_request_sent',
  'advance_signed_npi',
  'advance_signed_supervisor',
  'partial_tof_approved',
  'partial_tof_sent',
  'agreement_executed',
  'advance_sent_accounting',
  'reflected_ereports',
  'use_period_set',
  'form300_received',
  'clearance_signed_npi',
  'clearance_signed_supervisor',
  'clearance_sent',
]);
export type StipendSharedStepKey = z.infer<typeof StipendSharedStepKey>;

export const StipendMessageKind = z.enum(['phase1', 'form_reminder', 'signature_request', 'form300_reminder']);
export type StipendMessageKind = z.infer<typeof StipendMessageKind>;

// ---------------------------------------------------------------- params and queries

export const StipendCycleParams = z.strictObject({ cycleId: RowId });
/** An agreement or participant id. */
export const StipendIdParams = z.strictObject({ id: RowId });
export const StipendMemberParams = z.strictObject({ userId: RowId });
export const StipendStepParams = z.strictObject({ id: RowId, stepKey: StipendSharedStepKey });
export const StipendMessageParams = z.strictObject({ kind: StipendMessageKind });

export const StipendGridQuery = z.strictObject({ waitingOn: z.union([ChecklistOwner, z.literal('')]).optional() });
export type StipendGridQuery = z.input<typeof StipendGridQuery>;

/** format=json returns the rows instead of the CSV file. */
export const StipendAffiliationsQuery = z.strictObject({ format: z.string().optional() });
export type StipendAffiliationsQuery = z.input<typeof StipendAffiliationsQuery>;

export const StipendMessageQuery = z.strictObject({
  participantId: z.string().optional(),
  agreementId: z.string().optional(),
});
export type StipendMessageQuery = z.input<typeof StipendMessageQuery>;

// ---------------------------------------------------------------- requests

/** The amount is whole cents, checked by the service. */
export const StipendSettingsRequest = z.strictObject({
  stipendsEnabled: z.boolean().optional(),
  stipendAmountCents: z.union([z.number(), z.string()]).nullish(),
  stipendFormUrl: Text,
});
export type StipendSettingsRequest = z.input<typeof StipendSettingsRequest>;

/** No ids: the first ready agreements, at most 3. */
export const StipendBatchRequest = z.strictObject({ agreementIds: z.array(z.string()).nullish() });
export type StipendBatchRequest = z.input<typeof StipendBatchRequest>;

export const StipendAgreementRequest = z.strictObject({
  institutionName: Text,
  note: Text,
  signatoryContactId: Text,
  adminContactId: Text,
  supervisorContactId: Text,
});
export type StipendAgreementRequest = z.input<typeof StipendAgreementRequest>;

export const StipendStepCompleteRequest = ChecklistCompleteRequest;
export type StipendStepCompleteRequest = z.input<typeof StipendStepCompleteRequest>;

/** null follows the institution form. */
export const StipendRouteRequest = z.strictObject({ route: StipendRoute.nullish() });
export type StipendRouteRequest = z.input<typeof StipendRouteRequest>;

export const StipendSendRequest = z.strictObject({
  kind: StipendMessageKind,
  participantId: Text,
  agreementId: Text,
});
export type StipendSendRequest = z.input<typeof StipendSendRequest>;
